/**
 * Tag toast message utilities for consistent user feedback across the platform
 *
 * Provides centralized toast message generation for tag operations across
 * Assets, Technologies, Seeds, and Vulnerabilities sections. Ensures
 * consistent messaging patterns and proper pluralization.
 *
 * Message Patterns:
 * - Single entity: "Tag added to asset"
 * - Multiple entities: "Tag added to 5 assets"
 * - Errors: "Failed to add tag to asset: Network error"
 * - Validation: "Invalid tag: Tag cannot be empty"
 *
 * @example
 * ```typescript
 * // Success with single entity
 * toast.success(tagAddedSuccess('asset'));
 *
 * // Success with multiple entities
 * toast.success(tagAddedSuccess('asset', selectedKeys.length));
 *
 * // Error with details
 * toast.error(tagAddedError('technology', error.message));
 * ```
 */

/**
 * Entity types supported by tag operations
 *
 * Each type has singular and plural forms for proper message formatting.
 */
export type EntityType = 'asset' | 'technology' | 'seed' | 'vulnerability';

/**
 * Mapping of entity types to their plural forms
 *
 * Used for generating proper grammar in bulk operation messages.
 * Technologies is an irregular plural (not just adding 's').
 */
const ENTITY_PLURALS: Record<EntityType, string> = {
  asset: 'assets',
  technology: 'technologies',
  seed: 'seeds',
  vulnerability: 'vulnerabilities',
};

/**
 * Get the appropriate entity name (singular or plural) based on count
 *
 * @param entityType - Type of entity
 * @param count - Number of entities (undefined for single)
 * @returns Properly formatted entity name
 *
 * @example
 * getEntityName('asset')      // 'asset'
 * getEntityName('asset', 1)   // 'asset'
 * getEntityName('asset', 5)   // 'assets'
 * getEntityName('technology', 3) // 'technologies'
 */
function getEntityName(entityType: EntityType, count?: number): string {
  // If count is undefined or 1, use singular
  // Otherwise use plural
  if (count === undefined || count === 1) {
    return entityType;
  }
  return ENTITY_PLURALS[entityType];
}

/**
 * Success message for adding tag
 *
 * Generates appropriate success message based on whether operation
 * affected single or multiple entities. Automatically handles
 * pluralization and count formatting.
 *
 * @param entityType - Type of entity (asset, technology, etc.)
 * @param count - Number of entities affected (undefined for single)
 * @returns Toast message string
 *
 * @example
 * tagAddedSuccess('asset')           // 'Tag added to asset'
 * tagAddedSuccess('asset', 1)        // 'Tag added to asset'
 * tagAddedSuccess('asset', 5)        // 'Tag added to 5 assets'
 * tagAddedSuccess('technology', 3)   // 'Tag added to 3 technologies'
 */
export function tagAddedSuccess(
  entityType: EntityType,
  count?: number
): string {
  const entityName = getEntityName(entityType, count);

  if (count === undefined || count === 1) {
    return `Tag added to ${entityName}`;
  }

  return `Tag added to ${count} ${entityName}`;
}

/**
 * Success message for removing tag
 *
 * Generates appropriate success message for tag removal operations.
 * Follows same pattern as tagAddedSuccess with proper pluralization.
 *
 * @param entityType - Type of entity (asset, technology, etc.)
 * @param count - Number of entities affected (undefined for single)
 * @returns Toast message string
 *
 * @example
 * tagRemovedSuccess('seed')            // 'Tag removed from seed'
 * tagRemovedSuccess('seed', 1)         // 'Tag removed from seed'
 * tagRemovedSuccess('seed', 10)        // 'Tag removed from 10 seeds'
 * tagRemovedSuccess('vulnerability', 2) // 'Tag removed from 2 vulnerabilities'
 */
export function tagRemovedSuccess(
  entityType: EntityType,
  count?: number
): string {
  const entityName = getEntityName(entityType, count);

  if (count === undefined || count === 1) {
    return `Tag removed from ${entityName}`;
  }

  return `Tag removed from ${count} ${entityName}`;
}

/**
 * Error message for adding tag
 *
 * Generates error message for failed tag addition operations.
 * Optionally includes specific error details from backend or
 * network failures.
 *
 * @param entityType - Type of entity (asset, technology, etc.)
 * @param error - Optional error details from exception
 * @returns Toast error message string
 *
 * @example
 * tagAddedError('asset')                    // 'Failed to add tag to asset'
 * tagAddedError('technology', 'Network timeout') // 'Failed to add tag to technology: Network timeout'
 * tagAddedError('seed', error.message)      // 'Failed to add tag to seed: [error details]'
 */
export function tagAddedError(entityType: EntityType, error?: string): string {
  const baseMessage = `Failed to add tag to ${entityType}`;

  // Only append error details if error is non-empty
  if (error && error.trim().length > 0) {
    return `${baseMessage}: ${error}`;
  }

  return baseMessage;
}

/**
 * Error message for removing tag
 *
 * Generates error message for failed tag removal operations.
 * Follows same pattern as tagAddedError with optional error details.
 *
 * @param entityType - Type of entity (asset, technology, etc.)
 * @param error - Optional error details from exception
 * @returns Toast error message string
 *
 * @example
 * tagRemovedError('vulnerability')          // 'Failed to remove tag from vulnerability'
 * tagRemovedError('asset', 'Permission denied') // 'Failed to remove tag from asset: Permission denied'
 * tagRemovedError('seed', error.message)    // 'Failed to remove tag from seed: [error details]'
 */
export function tagRemovedError(
  entityType: EntityType,
  error?: string
): string {
  const baseMessage = `Failed to remove tag from ${entityType}`;

  // Only append error details if error is non-empty
  if (error && error.trim().length > 0) {
    return `${baseMessage}: ${error}`;
  }

  return baseMessage;
}

/**
 * Validation error message
 *
 * Generates validation error message for invalid tag input.
 * Used in conjunction with tagValidation.ts utilities to
 * provide user feedback on validation failures.
 *
 * @param error - Validation error message from validateTagInput
 * @returns Formatted toast error message
 *
 * @example
 * const result = validateTagInput(tag);
 * if (!result.isValid) {
 *   toast.error(tagValidationError(result.error!));
 * }
 *
 * // Produces messages like:
 * // "Invalid tag: Tag cannot be empty"
 * // "Invalid tag: Tag must start with a letter or number"
 * // "Invalid tag: Tag cannot exceed 50 characters"
 */
export function tagValidationError(error: string): string {
  return `Invalid tag: ${error}`;
}

/**
 * Success message for bulk tag add operations
 *
 * Generates summary message for bulk operations showing
 * how many entities were successfully updated and how many failed.
 * Useful for operations using Promise.allSettled pattern.
 *
 * @param entityType - Type of entity
 * @param successCount - Number of successful operations
 * @param totalCount - Total number of attempted operations
 * @returns Formatted toast message
 *
 * @example
 * tagBulkAddSuccess('asset', 8, 10)
 * // "Tag added to 8 of 10 assets"
 *
 * tagBulkAddSuccess('technology', 5, 5)
 * // "Tag added to 5 technologies"
 */
export function tagBulkAddSuccess(
  entityType: EntityType,
  successCount: number,
  totalCount: number
): string {
  const entityName = ENTITY_PLURALS[entityType];

  // If all succeeded, use simpler message
  if (successCount === totalCount) {
    return tagAddedSuccess(entityType, successCount);
  }

  // Show partial success
  return `Tag added to ${successCount} of ${totalCount} ${entityName}`;
}

/**
 * Success message for bulk tag remove operations
 *
 * Generates summary message for bulk removal operations showing
 * partial success when some operations fail.
 *
 * @param entityType - Type of entity
 * @param successCount - Number of successful operations
 * @param totalCount - Total number of attempted operations
 * @returns Formatted toast message
 *
 * @example
 * tagBulkRemoveSuccess('seed', 7, 8)
 * // "Tag removed from 7 of 8 seeds"
 *
 * tagBulkRemoveSuccess('vulnerability', 10, 10)
 * // "Tag removed from 10 vulnerabilities"
 */
export function tagBulkRemoveSuccess(
  entityType: EntityType,
  successCount: number,
  totalCount: number
): string {
  const entityName = ENTITY_PLURALS[entityType];

  // If all succeeded, use simpler message
  if (successCount === totalCount) {
    return tagRemovedSuccess(entityType, successCount);
  }

  // Show partial success
  return `Tag removed from ${successCount} of ${totalCount} ${entityName}`;
}
